import { identity, pickBy } from 'lodash-es'

import queryBuilder from '@rewardops/api/src/query-builder'
import {
  useMember,
  useMemberTags,
  useSegmentCode,
  useSession,
} from '@rewardops/auth'
import { useLocale } from '@rewardops/ui'

import { useApi } from './use-api'

type UseRecommendedItemsProps = {
  numberOfResults?: number
  minScore?: number
  recommendationType?: string
  filterType?: string
  filterValues?: string
  boostStrategyId?: string
  boostStrategyValues?: string
  boostInfluencePercent?: number
}

const useRecommendedItems = ({
  numberOfResults,
  minScore,
  recommendationType,
  filterType,
  filterValues,
  boostStrategyId,
  boostStrategyValues,
  boostInfluencePercent,
}: UseRecommendedItemsProps) => {
  const { api, tenant } = useApi()
  const { data: session } = useSession()
  const { locale } = useLocale()
  const segmentCode = useSegmentCode()
  const memberTags = useMemberTags()
  const member = useMember()

  const { data: itemRecommendations, isLoading: itemRecommendationsLoading } =
    api.itemRecommendations.get.useQuery(
      pickBy(
        {
          num_results: numberOfResults,
          min_score: minScore,
          recommendation_type: Boolean(session)
            ? recommendationType
            : tenant?.defaultRecommendationType,
          filter_type: filterType,
          filter_values: filterValues,
          boost_strategy_id: boostStrategyId,
          boost_strategy_values: boostStrategyValues?.toString(),
          boost_influence_percent: boostInfluencePercent,
          max_points:
            member?.points && member?.points > 0 ? member?.points : undefined,
        },
        identity,
      ),
      {
        enabled: tenant?.flags?.recommendedItems,
      },
    )

  const canFetchItems =
    !itemRecommendationsLoading && Boolean(itemRecommendations?.length)

  const getIvIds = (itemRecommendations: any) =>
    itemRecommendations?.map((item: any) => Number(item?.itemId))

  const { data: itemsResponse, isLoading: itemsLoading } =
    api.items.getAll.useQuery(
      queryBuilder.items.getAllWithFilter(
        {
          ivIds: itemRecommendations?.length
            ? getIvIds(itemRecommendations)
            : [],
        },
        tenant,
        {
          segment_code: segmentCode,
          accept_language: locale,
          ...(memberTags && { member_tags: memberTags }),
        },
        1,
        numberOfResults ?? 20,
      ),
      {
        enabled: canFetchItems,
      },
    )

  return {
    items: itemsResponse?.result?.items ?? [],
    loading: itemsLoading && canFetchItems,
  }
}

export { useRecommendedItems }
